
import React from 'react';
import MainLayout from '@/components/Layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import AnimateInView from '@/components/ui/animate-in-view';

const Industries = () => {
  // Industry categories with their solutions
  const industries = [
    {
      title: "Private Property",
      description: "Premium security solutions for high-value private properties, providing discrete protection without compromising aesthetics.",
      solutions: {
        fence: ["Private Estates", "High-value Storage Locations", "High Security Compounds"],
        fibre: ["Large Estates", "Country Clubs", "Golf Clubs", "Compounds"]
      }
    },
    {
      title: "Critical Infrastructure",
      description: "Comprehensive perimeter protection for facilities that require constant monitoring and immediate threat detection.",
      solutions: {
        fence: ["Warehousing", "Logistics Hubs", "Server Facilities"],
        fibre: ["Energy Utilities", "Petroleum and Chemical", "Substations", "Solar Farms", "Wind Farms"]
      }
    },
    {
      title: "Healthcare & Government",
      description: "Advanced security measures for sensitive and high-security facilities requiring regulatory compliance.",
      solutions: {
        fence: ["Healthcare Facilities", "Government Buildings", "Embassies"],
        fibre: ["Military Installations", "Government Buildings", "Critical Access Points", "Borders"]
      }
    },
    {
      title: "Commercial & Enterprise",
      description: "Tailored security solutions for businesses protecting valuable assets and ensuring operational continuity.",
      solutions: {
        fence: ["Commercial Real Estate", "Data Centers"],
        fibre: ["Manufacturing Centers", "Bank Call Centers", "Airports", "Sea Ports"]
      }
    },
    {
      title: "Agriculture & Resources",
      description: "Specialized protection for agricultural assets and remote resource operations.",
      solutions: {
        fence: ["Farm Perimeters", "Equipment Storage"],
        fibre: ["High-Value Agriculture", "Remediating Sites", "Mines", "Irrigation Systems"]
      }
    }
  ];

  return (
    <MainLayout>
      {/* Hero Section - Removed icon as requested */}
      <section className="bg-theme-solutions-bg py-16 px-4 bg-gradient-to-r from-forti-fi-background to-forti-fi-lavender/30" aria-labelledby="industries-heading">
        <div className="container mx-auto max-w-4xl text-center">
          <AnimateInView animation="fade-in">
            <h1 id="industries-heading" className="mb-4">Industries We Serve</h1>
            <p className="text-forti-fi-charcoal text-xl md:text-2xl mb-6 max-w-3xl mx-auto">
              Specialized security solutions tailored to the unique challenges of various industries and environments
            </p>
          </AnimateInView>
        </div>
      </section>

      {/* Industries Section - Alternating layout */}
      <section className="py-12 bg-white" aria-labelledby="industry-categories">
        <div className="container mx-auto px-4">
          <h2 id="industry-categories" className="text-center mb-12 text-forti-fi">Industry Sectors</h2>
          
          <div className="space-y-16">
            {industries.map((industry, index) => (
              <div key={index} className="mb-8">
                {/* Mobile View - Always stack vertically in the correct order */}
                <div className="lg:hidden space-y-6">
                  {/* Industry Description always first in mobile */}
                  <div>
                    <h3 className="text-2xl mb-4 text-forti-fi">{industry.title}</h3>
                    <p className="mb-6 text-forti-fi-charcoal">{industry.description}</p>
                  </div>
                  
                  {/* Solutions for this Industry always second in mobile */}
                  <div className="bg-forti-fi-background rounded-lg p-6">
                    <h4 className="text-xl mb-6 font-medium text-forti-fi border-b pb-2">Applied Solutions for {industry.title}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Fence Solutions */}
                      <div className="space-y-4">
                        <h5 className="font-medium text-forti-fi-teal">On-Fence Detection</h5>
                        <ul className="space-y-2">
                          {industry.solutions.fence.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        <Button asChild variant="outline" size="sm" className="mt-2">
                          <Link to="/solutions/fence">
                            Learn More <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                      
                      {/* Fibre Solutions */}
                      <div className="space-y-4">
                        <h5 className="font-medium text-forti-fi-teal">Fibre-Optic Perimeter</h5>
                        <ul className="space-y-2">
                          {industry.solutions.fibre.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        <Button asChild variant="outline" size="sm" className="mt-2">
                          <Link to="/solutions/fibre">
                            Learn More <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Desktop View - Alternating layout */}
                <AnimateInView 
                  animation={index % 2 === 0 ? "slide-left" : "slide-right"} 
                  className="hidden lg:grid lg:grid-cols-5 gap-8 items-start"
                >
                  {index % 2 === 0 ? (
                    // Even indexes: Industry on left, Solutions on right
                    <>
                      {/* Industry Description */}
                      <div className="lg:col-span-2">
                        <h3 className="text-2xl mb-4 text-forti-fi">{industry.title}</h3>
                        <p className="mb-6 text-forti-fi-charcoal">{industry.description}</p>
                      </div>
                      
                      {/* Solutions for this Industry */}
                      <div className="lg:col-span-3 bg-forti-fi-background rounded-lg p-6">
                        <h4 className="text-xl mb-6 font-medium text-forti-fi border-b pb-2">Applied Solutions for {industry.title}</h4>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Fence Solutions */}
                          <div className="space-y-4">
                            <h5 className="font-medium text-forti-fi-teal">On-Fence Detection</h5>
                            <ul className="space-y-2">
                              {industry.solutions.fence.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                            <Button asChild variant="outline" size="sm" className="mt-2">
                              <Link to="/solutions/fence">
                                Learn More <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                          
                          {/* Fibre Solutions */}
                          <div className="space-y-4">
                            <h5 className="font-medium text-forti-fi-teal">Fibre-Optic Perimeter</h5>
                            <ul className="space-y-2">
                              {industry.solutions.fibre.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                            <Button asChild variant="outline" size="sm" className="mt-2">
                              <Link to="/solutions/fibre">
                                Learn More <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Odd indexes: Solutions on left, Industry on right
                    <>
                      {/* Solutions for this Industry */}
                      <div className="lg:col-span-3 bg-forti-fi-background rounded-lg p-6">
                        <h4 className="text-xl mb-6 font-medium text-forti-fi border-b pb-2">Applied Solutions for {industry.title}</h4>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Fence Solutions */}
                          <div className="space-y-4">
                            <h5 className="font-medium text-forti-fi-teal">On-Fence Detection</h5>
                            <ul className="space-y-2">
                              {industry.solutions.fence.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                            <Button asChild variant="outline" size="sm" className="mt-2">
                              <Link to="/solutions/fence">
                                Learn More <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                          
                          {/* Fibre Solutions */}
                          <div className="space-y-4">
                            <h5 className="font-medium text-forti-fi-teal">Fibre-Optic Perimeter</h5>
                            <ul className="space-y-2">
                              {industry.solutions.fibre.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle className="text-forti-fi-teal mr-2 h-5 w-5 shrink-0 mt-0.5" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                            <Button asChild variant="outline" size="sm" className="mt-2">
                              <Link to="/solutions/fibre">
                                Learn More <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Industry Description */}
                      <div className="lg:col-span-2">
                        <h3 className="text-2xl mb-4 text-forti-fi">{industry.title}</h3>
                        <p className="mb-6 text-forti-fi-charcoal">{industry.description}</p>
                      </div>
                    </>
                  )}
                </AnimateInView>
              </div>
            ))}
          </div>
          
          {/* Custom Solutions CTA */}
          <div className="mt-16 text-center">
            <AnimateInView animation="fade-in">
              <h3 className="text-2xl mb-4 text-forti-fi">Need a Custom Security Solution?</h3>
              <p className="mb-6 max-w-2xl mx-auto text-forti-fi-charcoal">
                Every property has unique security challenges. Our team specializes in developing bespoke 
                security solutions tailored to your specific requirements.
              </p>
              <Button asChild variant="gradient" className="text-white">
                <Link to="/solutions/bespoke">
                  Explore Custom Solutions
                </Link>
              </Button>
            </AnimateInView>
          </div>
        </div>
      </section>
      
      {/* Consultation CTA */}
      <section className="bg-forti-fi-dark text-white py-12 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <AnimateInView animation="fade-in">
            <h2 className="text-white mb-4">Discuss Your Industry Needs</h2>
            <p className="text-forti-fi-lavender mb-6 text-lg">
              Speak with our security experts to learn how our solutions can be tailored 
              to your industry's specific challenges and compliance requirements.
            </p>
            <Button asChild size="lg" variant="gradient" className="text-white">
              <Link to="/contact">
                Schedule an Industry Consultation
              </Link>
            </Button>
          </AnimateInView>
        </div>
      </section>
    </MainLayout>
  );
};

export default Industries;
